import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import MailIcon from '@mui/icons-material/Mail';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ReplayIcon from '@mui/icons-material/Replay';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { getOrganizationId } from '../../store';

import { getRoleLabel } from '../../utils/roles';
// Map event types to icons and colors
const eventConfig = {
  permission_created: { icon: PersonAddIcon, color: 'success' },
  role_changed: { icon: SwapHorizIcon, color: 'info' },
  permission_deleted: { icon: PersonRemoveIcon, color: 'error' },
  invitation_created: { icon: MailIcon, color: 'info' },
  invitation_accepted: { icon: CheckCircleIcon, color: 'success' },
  invitation_revoked: { icon: CancelIcon, color: 'error' },
  invitation_resent: { icon: ReplayIcon, color: 'info' },
};

function PermissionAuditLogDialog({ open, handleClose }) {
  const { t, i18n } = useTranslation();
  const organizationId = getOrganizationId();
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && organizationId) {
      fetchEvents();
    }
  }, [open, organizationId]);

  const fetchEvents = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get('/api/organization-permission-logs/', {
        headers: { 'X-Organization-Id': organizationId },
      });
      setEvents(response.data);
    } catch (err) {
      setError(err.response?.data?.error || t('permissionLog.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const locale = i18n.language === 'fi' ? 'fi-FI' : 'en-US';
    return date.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Group events by date
  const groupEventsByDate = (events) => {
    const groups = {};
    events.forEach((event) => {
      const date = new Date(event.created_at);
      const locale = i18n.language === 'fi' ? 'fi-FI' : 'en-US';
      const dateKey = date.toLocaleDateString(locale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(event);
    });
    return groups;
  };

  const getEventIcon = (eventType) => {
    const config = eventConfig[eventType] || { icon: HistoryIcon, color: 'default' };
    return config;
  };

  const roleLabel = (role) => getRoleLabel(role, t);

  // Generate translated description from event type and details
  const getEventDescription = (event) => {
    const { event_type, details, target_user_name, target_user_email, target_email } = event;
    const targetName = target_user_name || target_user_email || target_email || t('permissionLog.unknownUser');

    switch (event_type) {
      case 'permission_created':
        return t('permissionLog.permissionCreated', {
          name: targetName,
          role: roleLabel(details?.role),
        });

      case 'role_changed':
        return t('permissionLog.roleChanged', {
          name: targetName,
          oldRole: roleLabel(details?.old_role),
          newRole: roleLabel(details?.new_role),
        });

      case 'permission_deleted':
        return t('permissionLog.permissionDeleted', {
          name: targetName,
        });

      case 'invitation_created':
        return t('permissionLog.invitationCreated', {
          email: target_email,
          role: roleLabel(details?.role),
        });

      case 'invitation_accepted':
        return t('permissionLog.invitationAccepted', {
          name: targetName,
        });

      case 'invitation_revoked':
        return t('permissionLog.invitationRevoked', {
          email: target_email,
        });

      case 'invitation_resent':
        return t('permissionLog.invitationResent', {
          email: target_email,
        });

      default:
        return event.description || t('permissionLog.unknownEvent');
    }
  };

  const renderEvent = (event) => {
    const config = getEventIcon(event.event_type);
    const IconComponent = config.icon;

    return (
      <Box
        key={event.id}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          py: 1.5,
          '&:not(:last-child)': {
            borderBottom: 1,
            borderColor: 'divider',
          },
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: `${config.color}.lighter`,
            flexShrink: 0,
          }}
        >
          <IconComponent
            sx={{
              fontSize: 18,
              color: `${config.color}.main`,
            }}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {getEventDescription(event)}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {formatDate(event.created_at)}
            </Typography>
            {event.performed_by_name && (
              <>
                <Typography variant="caption" color="text.disabled">
                  •
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {event.performed_by_name}
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  const groupedEvents = groupEventsByDate(events);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: '12px', sm: '16px' },
          m: { xs: 2, sm: 4 },
          maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon color="primary" />
          <Typography sx={{ fontWeight: 600, fontSize: '18px' }}>
            {t('permissionLog.title')}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>
            {error}
          </Alert>
        )}

        {!loading && !error && events.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <HistoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">{t('permissionLog.noEvents')}</Typography>
          </Box>
        )}

        {!loading && !error && events.length > 0 && (
          <Box>
            {Object.entries(groupedEvents).map(([date, dateEvents], index) => (
              <Box key={date}>
                {index > 0 && <Divider sx={{ my: 2 }} />}
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 600,
                    mb: 1,
                    textTransform: 'uppercase',
                    fontSize: '11px',
                    letterSpacing: '0.5px',
                  }}
                >
                  {date}
                </Typography>
                {dateEvents.map(renderEvent)}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default PermissionAuditLogDialog;
